import { useEffect, useState } from "react";
import { TextArea } from "semantic-ui-react";
import {
  getBulkDropdownData,
  getDropdownData,
  getFieldValuesByTableAndId,
  getTextByValueAndKey,
} from "../../../api/keyvalue";
import LoadingSpinner from "../../LoadingSpinner";
import Grid from "../../Grid";
import ButtonRenderer from "../../renderer/ButtonRenderer";
import { negotiationFormValidation } from "../../validations/jobMonitor";
import {
  createJobOther,
  getNegotiationCharge,
  jobOtherByJobId,
  updateJobOther,
} from "../../../api/jobApi";
import { getChargeTypeDetails } from "../../../api/finance/chargeTypeApi";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";
import Dropdown from "../../input/Dropdown";
import Input from "../../input/Input";
import Button from "../../input/Button";
import { AUTH_TYPES, FORMS, MODULES, STATUS_GROUPS } from "../../../const";
import { hasMultiple } from "../../../common/logic";
import { isNumber } from "../../../helper";
import { chargesByCustomer } from "../../../api/customerChargesApi";
import { statusIdsByContexts } from "../../../api/statusApi";
import { ResizableScrollingTextRenderer } from "../../renderer/StyleRenderer";
import DeleteButtonRenderer from "../../renderer/DeleteButtonRenderer";

const form = FORMS.JOB.NEGOTIATION.form;

const JobMonitorNegotiation = ({
  type,
  jobId,
  jobCode,
  requestId,
  requestCode,
  stakeholder,
  close,
  customer,
  currentStatus,
}) => {
  const user = useSelector((state) => state.user);
  const auth = useSelector(
    (state) => state.user.permissions[MODULES.Transaction],
  );

  const negotiationPermission = auth[`${form}_${type}`];
  const addAfterCompletionEnabled =
    negotiationPermission?.FORM?.ADD_AFTER_COMPLETION;
  const [currentErrors, setCurrentErrors] = useState({});

  const [stakeholderTypeOptions, setStakeholderTypeOptions] = useState([]);
  const [chargeCategoryOptions, setChargeCategoryOptions] = useState([]);
  const [chargeTypeOptions, setChargeTypeOptions] = useState([]);
  const [chargeModeOptions, setChargeModeOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [activeOptions, setActiveOptions] = useState([]);
  const [currentValues, setCurrentValues] = useState([]);
  const [editMode, setEditMode] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const [negotiations, setNegotiations] = useState([]);
  const [currentNegotiation, setCurrentNegotiation] = useState({});
  const [negotiationCount, setNegotiationCount] = useState(2);
  const [buttonClicked, setButtonClicked] = useState(false);
  const [isAddLoading, setIsAddLoading] = useState(false);
  const [isCustomerLoading, setIsCustomerLoading] = useState(false);
  const [deletedList, setDeletedList] = useState([]);
  const [stakeholderCode, setSetStakeholderCode] = useState("");
  const [stakeholderName, setStakeholderName] = useState("");
  const [cusSupNegotiationEnabledList, setCusSupNegotiationEnabledList] =
    useState([]);

  // Store charge metadata for conditional field enabling
  const [chargeMetadata, setChargeMetadata] = useState({});
  const [selectedChargeQtyChargeType, setSelectedChargeQtyChargeType] =
    useState(null);

  const jobCompletedNegotiation =
    auth[FORMS.JOB.JOB_COMPLETED_NEGOTIATION.form]?.FORM?.CREATE;

  const stakeholderAvailable = stakeholder !== null;

  const [negotiationData, setNegotiationData] = useState({
    jotCode: "",
    jotRequest: requestId,
    jotRequestCode: requestCode,
    jotJob: jobId,
    jotJobCode: jobCode,
    jotStakeType: type,
    jotStakeholder: stakeholder,
    jotStakeholderCode: stakeholderCode,
    jotStakeholderName: "",
    jotChargeCategory: "",
    jotChargeCategoryValue: "",
    jotChargeType: "",
    jotChargeTypeValue: "",
    jotChargeMode: "",
    jotChargeModeValue: "",
    jotQty: "",
    jotCharge: "",
    jotStatus: type === 1 ? 2 : 1,
    jotActive: 1,
    jotActiveValue: "",
    jotDescription: "",
    jotTypeValue: "",
  });

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      // new
      const {
        stakeholder_type: typeData,
        chg_category: chargeCategoryData,
        chg_mode: chargeModeData,
        status: statusData,
        active: activeData,
      } = await getBulkDropdownData([
        { key: "stakeholder_type" },
        {
          key: "chg_category",
          authenticated: true,
          authorities: [
            "NEGOTIATION_FORM_ADD_REVENUE",
            "NEGOTIATION_FORM_ADD_COST",
            "NEGOTIATION_FORM_ADD_FOC",
          ],
        },
        { key: "chg_mode" },
        { key: "status" },
        { key: "active" },
      ]);

      setStakeholderTypeOptions(typeData);
      setChargeCategoryOptions(chargeCategoryData);
      setChargeModeOptions(chargeModeData);
      setStatusOptions(statusData);
      setActiveOptions(activeData);

      if (stakeholderAvailable) {
        const stakeholderRes = await getFieldValuesByTableAndId({
          tableName: "masterdata.tms_m_stakeholder",
          primaryKey: "stk_id",
          ids: stakeholder.toString(),
          fieldNames: "stk_code, stk_name",
        });

        const jotStakeholderCode = stakeholderRes[0].field1;
        const jotStakeholderName = stakeholderRes[0].field2;

        setSetStakeholderCode(jotStakeholderCode);
        setStakeholderName(jotStakeholderName);
        setNegotiationData((prevState) => ({
          ...prevState,
          jotStakeholderCode: jotStakeholderCode,
          jotStakeholderName: jotStakeholderName,
        }));
      } else {
        setCurrentErrors((prevState) => ({
          ...prevState,
          jotStakeholder: `${
            type === 1 ? "Customer" : "Supplier"
          } not available for this job`,
        }));
      }

      const {
        [type === 1
          ? STATUS_GROUPS.CUSTOMER_NEGOTIATION_ENABLED
          : STATUS_GROUPS.SUPPLIER_NEGOTIATION_ENABLED]:
          cuSuNegotiationEnableList = [],
        [type === 1
          ? STATUS_GROUPS.CUSTOMER_NEGOTIATION_ENABLED_AFTER_COMPLETION
          : STATUS_GROUPS.SUPPLIER_NEGOTIATION_ENABLED_AFTER_COMPLETION]:
          cuSuNegotiationEnableListAfterComp = [],
      } = await statusIdsByContexts([
        type === 1
          ? STATUS_GROUPS.CUSTOMER_NEGOTIATION_ENABLED
          : STATUS_GROUPS.SUPPLIER_NEGOTIATION_ENABLED,
        type === 1
          ? STATUS_GROUPS.CUSTOMER_NEGOTIATION_ENABLED_AFTER_COMPLETION
          : STATUS_GROUPS.SUPPLIER_NEGOTIATION_ENABLED_AFTER_COMPLETION,
      ]);

      setCusSupNegotiationEnabledList(
        addAfterCompletionEnabled
          ? cuSuNegotiationEnableListAfterComp
          : cuSuNegotiationEnableList,
      );

      const negotiationData = await jobOtherByJobId(jobId, parseInt(type));
      setEditMode(negotiationData !== null && negotiationData.length !== 0);
      setCurrentValues(negotiationData);

      const negotiationCharges = await getNegotiationCharge(type);
      console.log("negotiation Type - Full Response:", negotiationCharges);
      console.log("negotiation Type - First Item:", negotiationCharges?.[0]);
      if (negotiationCharges && Array.isArray(negotiationCharges)) {
        // Build metadata map for charge types to track Qty/Charge types
        const chargeMetadataMap = {};

        // Fetch full details for each charge to get chtQtyCharge
        const enrichedCharges = await Promise.all(
          negotiationCharges.map(async (item) => {
            try {
              const fullDetails = await getChargeTypeDetails(item.chtId);
              return {
                ...item,
                chtQtyCharge: fullDetails?.chtQtyCharge || item.chtQtyCharge,
              };
            } catch (error) {
              console.error(
                `Error fetching details for charge ${item.chtId}:`,
                error,
              );
              return item;
            }
          }),
        );

        const formattedOptions = enrichedCharges.map((item) => {
          console.log("Processing charge item:", { id: item.chtId, item });
          chargeMetadataMap[item.chtId] = {
            chtQtyCharge: item.chtQtyCharge,
            chtCharge: item.chtCharge,
          };
          return {
            key: item.chtId,
            value: item.chtId,
            text: item.chtCharge,
          };
        });
        console.log("Final chargeMetadataMap:", chargeMetadataMap);
        setChargeMetadata(chargeMetadataMap);
        setChargeTypeOptions(formattedOptions);
      } else {
        setChargeTypeOptions([]);
        setChargeMetadata({});
      }

      setIsLoading(false);
    })();
  }, [jobId, type, stakeholder, stakeholderAvailable]);

  useEffect(() => {
    (async () => {
      if (editMode) {
        let currentNegotiations = [];
        let id = 2;

        for (let currentValue of currentValues) {
          const jotChargeCategoryValue = await getTextByValueAndKey(
            "chg_category",
            currentValue.jotChargeCategory,
          );
          const jotChargeModeValue = await getTextByValueAndKey(
            "chg_mode",
            currentValue.jotChargeMode,
          );
          const jotActiveValue = await getTextByValueAndKey(
            "active",
            currentValue.jotActive,
          );

          const negotiationCharge = await getNegotiationCharge(type);
          const selectedCharge = negotiationCharge.find(
            (item) => item.chtId === currentValue.jotChargeType,
          );
          const jotChargeTypeValue = selectedCharge
            ? selectedCharge.chtCharge
            : "";

          currentNegotiations.push({
            ...currentValue,
            jotRequest: currentValue.jotRequestId,
            jotJob: currentValue.jotJobId,
            jotStakeholder: currentValue.jotStakeholderId,
            jotDescription: currentValue.jotDescriptionn,
            jotChargeCategoryValue,
            jotChargeModeValue,
            jotActiveValue,
            jotChargeTypeValue,
            id,
          });
          id++;
        }
        setNegotiationCount(id);
        setNegotiations(currentNegotiations);
      }
    })();
  }, [editMode, currentValues]);

  useEffect(() => {
    if (buttonClicked) {
      setNegotiationData({
        jotCode: "",
        jotRequest: requestId,
        jotRequestCode: requestCode,
        jotJob: jobId,
        jotJobCode: jobCode,
        jotStakeType: type,
        jotStakeholder: stakeholder,
        jotStakeholderCode: stakeholderCode,
        jotStakeholderName: "",
        jotChargeCategory: "",
        jotChargeCategoryValue: "",
        jotChargeType: "",
        jotChargeTypeValue: "",
        jotChargeMode: "",
        jotChargeModeValue: "",
        jotQty: "",
        jotCharge: "",
        jotStatus: type === 1 ? 2 : 1,
        jotActive: 1,
        jotActiveValue: "",
        jotDescription: "",
        jotTypeValue: "",
      });
      setCurrentNegotiation({});
      setCurrentErrors({});
      setButtonClicked(false);
    }
  }, [
    buttonClicked,
    jobId,
    requestId,
    stakeholder,
    stakeholderCode,
    jobCode,
    requestCode,
    type,
  ]);

  useEffect(() => {
    if (Object.keys(currentNegotiation).length > 0) {
      setNegotiationData({
        ...currentNegotiation,
        jotChargeCategoryValue: "",
        jotChargeTypeValue: "",
        jotChargeModeValue: "",
        jotActiveValue: "",
        jotTypeValue: "",
      });
    }
  }, [currentNegotiation]);

  /**
   * Helper function to determine if a field should be enabled based on charge type
   * @param {number} chargeId - The selected charge type ID
   * @param {string} fieldType - Either 'qty' or 'charge'
   * @returns {boolean} - true if field should be enabled
   */
  const isFieldEnabled = (chargeId, fieldType) => {
    if (!chargeId || !chargeMetadata[chargeId]) {
      console.log("isFieldEnabled debug:", {
        chargeId,
        metadata: chargeMetadata,
        chargeExists: !!chargeMetadata[chargeId],
      });
      return false;
    }

    const qtyChargeType = chargeMetadata[chargeId]?.chtQtyCharge;
    console.log("isFieldEnabled:", {
      chargeId,
      fieldType,
      qtyChargeType,
      metadata: chargeMetadata[chargeId],
    });

    // qtyChargeType can be: 1 for Qty, 2 for Charge, 3 for both
    if (fieldType === "qty") {
      return qtyChargeType === 1 || qtyChargeType === 3;
    } else if (fieldType === "charge") {
      return qtyChargeType === 2 || qtyChargeType === 3;
    }

    return false;
  };

  const submitHandler = async () => {
    if (
      !cusSupNegotiationEnabledList.includes(currentStatus) &&
      !jobCompletedNegotiation
    ) {
      toast.warn("Cannot create/update job negotiation in this job stage!", {
        containerId: "Modal",
        autoClose: 1000,
      });
      return;
    }
    setIsSubmitLoading(true);
    if (editMode) {
      const updateState = {
        modified: [],
        created: [],
        deleted: deletedList,
      };

      negotiations.forEach((jot) => {
        if (jot.jotId) {
          if (jot.modified) {
            updateState.modified.push(jot);
          }
        } else {
          updateState.created.push(jot);
        }
      });

      try {
        await updateJobOther({
          jobOtherRequest: updateState,
          author: user.username,
        });
        toast.success("job negotiation updated successfully!", {
          autoClose: 5000,
          containerId: "Modal",
        });
        close();
      } catch (e) {
        toast.error("error while updating job negotiation!", {
          containerId: "Modal",
          autoClose: 1000,
        });
      }
    } else {
      try {
        if (negotiations && negotiations.length === 0) {
          toast.error("No job negotiations to save!", {
            containerId: "Modal",
            autoClose: 1000,
          });
          close(false);
          return;
        }
        await createJobOther({
          jobOtherRequest: negotiations,
          author: user.username,
        });
        toast.success("job negotiation created successfully!", {
          containerId: "Modal",
          autoClose: 1000,
        });
        close();
      } catch (e) {
        toast.error("error while creating job negotiation!", {
          containerId: "Modal",
          autoClose: 1000,
        });
      }
    }
    setIsSubmitLoading(false);
  };

  // useEffect(() => {
  //   (async () => {
  //     setIsCustomerLoading(true);
  //     if (customer) {
  //       const data = await chargesByCustomer(customer);
  //       setChargeTypeOptions(data);
  //     } else {
  //       setChargeTypeOptions([]);
  //     }
  //     setIsCustomerLoading(false);
  //   })();
  // }, [customer]);

  const onAddClick = async () => {
    setIsAddLoading(true);
    const negotiationErrors = negotiationFormValidation(negotiationData);

    // Validate based on charge type configuration
    const qtyEnabled = isFieldEnabled(negotiationData.jotChargeType, "qty");
    const chargeEnabled = isFieldEnabled(
      negotiationData.jotChargeType,
      "charge",
    );

    const isQtyFilled =
      negotiationData.jotQty && parseFloat(negotiationData.jotQty) > 0;
    const isChargeFilled =
      negotiationData.jotCharge && parseFloat(negotiationData.jotCharge) > 0;

    // Check if required fields based on charge type are filled
    if (qtyEnabled && !isQtyFilled) {
      toast.error("Please enter Qty value", {
        containerId: "Modal",
        autoClose: 2000,
      });
      setIsAddLoading(false);
      return;
    }

    if (chargeEnabled && !isChargeFilled) {
      toast.error("Please enter Charge value", {
        containerId: "Modal",
        autoClose: 2000,
      });
      setIsAddLoading(false);
      return;
    }

    // If both are enabled, at least one must be filled
    if (qtyEnabled && chargeEnabled && !isQtyFilled && !isChargeFilled) {
      toast.error("Please enter either Qty or Charge", {
        containerId: "Modal",
        autoClose: 2000,
      });
      setIsAddLoading(false);
      return;
    }

    if (Object.keys(negotiationErrors).length === 0) {
      const nonUniqueRecord = hasMultiple({
        data: negotiations,
        newItem: negotiationData,
        validator: (negotiation) =>
          negotiation.jotChargeCategory === negotiationData.jotChargeCategory &&
          negotiation.jotChargeType === negotiationData.jotChargeType &&
          negotiation.jotChargeMode === negotiationData.jotChargeMode,
        persistentIdentifier: "jotId",
      });

      if (nonUniqueRecord) {
        toast.warn("Cannot add multiple records with same charge type", {
          containerId: "Modal",
          autoClose: 1000,
        });
        setIsAddLoading(false);
        return;
      }

      negotiationData.jotQty = negotiationData.jotQty?.toString().trim()
        ? parseFloat(negotiationData.jotQty)
        : 0;
      negotiationData.jotCharge = negotiationData.jotCharge?.toString().trim()
        ? parseFloat(negotiationData.jotCharge)
        : 0;

      const jotChargeCategoryValue = await getTextByValueAndKey(
        "chg_category",
        negotiationData.jotChargeCategory,
      );

      // const jotChargeTypeValue = await getFieldValuesByTableAndId({
      //   tableName: "masterdata.tms_vm_stakeholder_charge_types",
      //   primaryKey: "chp_id",
      //   fieldNames: "cht_charge",
      //   ids: negotiationData.jotChargeType,
      // });
      // const jotTypeValue = jotChargeTypeValue[0]?.field1 || "";

      const negotiationCharges = await getNegotiationCharge(type);
      const selectedCharge = negotiationCharges.find(
        (item) => item.chtId === negotiationData.jotChargeType,
      );

      const jotChargeTypeValue = selectedCharge ? selectedCharge.chtCharge : "";
      const jotTypeValue = jotChargeTypeValue;

      const jotChargeModeValue = await getTextByValueAndKey(
        "chg_mode",
        negotiationData.jotChargeMode,
      );
      const jotActiveValue = await getTextByValueAndKey(
        "active",
        negotiationData.jotActive,
      );
      if (!negotiationData.jotId) {
        if (!negotiationData.id) {
          setNegotiations((prevState) => [
            ...prevState,
            {
              ...negotiationData,
              jotStakeholderCode: stakeholderCode,
              jotChargeCategoryValue,
              jotChargeTypeValue,
              jotChargeModeValue,
              jotActiveValue,
              jotTypeValue,
              id: negotiationCount,
            },
          ]);
          setNegotiationCount((prevCount) => ++prevCount);
        } else {
          setNegotiations((prevState) =>
            prevState.filter((item) => item.id !== negotiationData.id),
          );
          setNegotiations((prevState) => [
            ...prevState,
            {
              ...negotiationData,
              jotStakeholderCode: stakeholderCode,
              jotChargeCategoryValue,
              jotChargeTypeValue,
              jotChargeModeValue,
              jotActiveValue,
              jotTypeValue,
            },
          ]);
        }
      } else {
        setNegotiations((prevState) =>
          prevState.filter((item) => item.jotId !== negotiationData.jotId),
        );
        setNegotiations((prevState) => [
          ...prevState,
          {
            ...negotiationData,
            jotStakeholderCode: stakeholderCode,
            jotChargeCategoryValue,
            jotChargeTypeValue,
            jotChargeModeValue,
            jotActiveValue,
            jotTypeValue,
            modified: true,
          },
        ]);
      }
      setButtonClicked(true);
    }
    setCurrentErrors(negotiationErrors);
    setIsAddLoading(false);
  };

  console.log(negotiations);

  return (
    <>
      {isLoading || isCustomerLoading ? (
        <LoadingSpinner />
      ) : (
        <form className="ui form" style={{ overflowY: "auto", height: "75vh" }}>
          <div className="ui segment">
            <h4 className="ui dividing header">
              {type === 1 ? "Customer" : "Supplier"} Negotiation
            </h4>
            <div className="ui segment">
              <div className="three fields">
                <Input
                  label="Negotiation code"
                  error={currentErrors.jotCode}
                  form={`${form}_${type}`}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { jotCode: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="NegotiationCode"
                  value={negotiationData.jotCode}
                  readOnly={true}
                />

                <Input
                  label="Request"
                  error={currentErrors.jotRequest}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { jotRequest: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="NegotiationRequest"
                  value={requestCode}
                  readOnly={true}
                />
                <Input
                  label="Job Id"
                  error={currentErrors.jotJob}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { jotJob: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="NegotiationJobId"
                  value={jobCode}
                  readOnly={true}
                />
              </div>

              <div className="two fields">
                <Dropdown
                  label="Stakeholder Type"
                  search={true}
                  error={currentErrors.jotStakeType}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { jotStakeType: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  placeholder="Stakeholder Type"
                  options={stakeholderTypeOptions}
                  value={type}
                  style={{ height: "20px" }}
                  readOnly={true}
                />

                <Input
                  label="Stakeholder Name"
                  error={currentErrors.jotStakeholder}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { jotStakeholder: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="jobStakeholder"
                  value={stakeholderName} // Use stakeholderName instead of stakeholderCode
                  readOnly={true}
                />
              </div>
            </div>

            <div className="three fields">
              <Dropdown
                required={true}
                search={true}
                label="Charge Category"
                error={currentErrors.jotChargeCategory}
                resetError={() =>
                  setCurrentErrors((prevState) => {
                    const { jotChargeCategory: _, ...rest } = prevState;
                    return rest;
                  })
                }
                placeholder="Charge Category"
                options={chargeCategoryOptions}
                value={negotiationData.jotChargeCategory}
                style={{ height: "20px" }}
                onChange={(e, data) => {
                  setNegotiationData((prevState) => ({
                    ...prevState,
                    jotChargeCategory: data.value,
                  }));
                }}
                onFocus={(e) => {
                  e.preventDefault();
                }}
              />
              <Dropdown
                required={true}
                label="Charge Type"
                search={true}
                error={currentErrors.jotChargeType}
                resetError={() =>
                  setCurrentErrors((prevState) => {
                    const { jotChargeType: _, ...rest } = prevState;
                    return rest;
                  })
                }
                placeholder="Charge Type"
                options={chargeTypeOptions}
                value={negotiationData.jotChargeType}
                style={{ height: "20px" }}
                onChange={(e, data) => {
                  // Track the selected charge's Qty/Charge type for conditional field enabling
                  console.log("Charge Type Changed:", {
                    selectedValue: data.value,
                    metadata: chargeMetadata,
                  });
                  setSelectedChargeQtyChargeType(
                    chargeMetadata[data.value]?.chtQtyCharge,
                  );
                  setNegotiationData((prevState) => ({
                    ...prevState,
                    jotChargeType: data.value,
                  }));
                }}
                onFocus={(e) => {
                  e.preventDefault();
                }}
              />
              <Dropdown
                required={true}
                search={true}
                label="Charge Mode"
                error={currentErrors.jotChargeMode}
                resetError={() =>
                  setCurrentErrors((prevState) => {
                    const { jotChargeMode: _, ...rest } = prevState;
                    return rest;
                  })
                }
                placeholder="Charge Mode"
                options={chargeModeOptions}
                value={negotiationData.jotChargeMode}
                style={{ height: "20px" }}
                onChange={(e, data) => {
                  setNegotiationData((prevState) => ({
                    ...prevState,
                    jotChargeMode: data.value,
                  }));
                }}
                onFocus={(e) => {
                  e.preventDefault();
                }}
              />
            </div>
            <div className="two fields">
              <Input
                type="number"
                label="Qty"
                error={currentErrors.jotQty}
                resetError={() =>
                  setCurrentErrors((prevState) => {
                    const { jotQty: _, ...rest } = prevState;
                    return rest;
                  })
                }
                name="jobQty"
                value={negotiationData.jotQty}
                onChange={(e) => {
                  setNegotiationData((prevState) => ({
                    ...prevState,
                    jotQty: e.target.value,
                  }));
                }}
                min="0"
                disabled={!isFieldEnabled(negotiationData.jotChargeType, "qty")}
                required={isFieldEnabled(negotiationData.jotChargeType, "qty")}
              />

              <Input
                type="number"
                label="Charge"
                error={currentErrors.jotCharge}
                resetError={() =>
                  setCurrentErrors((prevState) => {
                    const { jotCharge: _, ...rest } = prevState;
                    return rest;
                  })
                }
                name="jobCharge"
                value={negotiationData.jotCharge}
                onChange={(e) => {
                  setNegotiationData((prevState) => ({
                    ...prevState,
                    jotCharge: e.target.value,
                  }));
                }}
                min="0"
                disabled={
                  !isFieldEnabled(negotiationData.jotChargeType, "charge")
                }
                required={isFieldEnabled(
                  negotiationData.jotChargeType,
                  "charge",
                )}
              />
            </div>
            <div className="two fields">
              <Dropdown
                required={true}
                search={true}
                label="Status"
                disabled={type !== 1}
                error={currentErrors.jotStatus}
                resetError={() =>
                  setCurrentErrors((prevState) => {
                    const { jotStatus: _, ...rest } = prevState;
                    return rest;
                  })
                }
                placeholder="Status"
                options={statusOptions}
                value={negotiationData.jotStatus}
                style={{ height: "20px" }}
                onChange={(e, data) => {
                  setNegotiationData((prevState) => ({
                    ...prevState,
                    jotStatus: data.value,
                  }));
                }}
                onFocus={(e) => {
                  console.log("FOCUSED", e);
                }}
              />

              {/* <Dropdown
                required={true}
                search={true}
                label="Active"
                error={currentErrors.jotActive}
                editMode={editMode}
                module={MODULES.Transaction}
                form={`${form}_${type}`}
                field="nego_active"
                authenticated={true}
                needDeletePermission={true}
                resetError={() =>
                  setCurrentErrors((prevState) => {
                    const { jotActive: _, ...rest } = prevState;
                    return rest;
                  })
                }
                placeholder="Active"
                options={activeOptions}
                value={negotiationData.jotActive}
                style={{ height: "20px" }}
                onChange={(e, data) => {
                  setNegotiationData((prevState) => ({
                    ...prevState,
                    jotActive: data.value,
                  }));
                }}
                onFocus={(e) => {
                  console.log("FOCUSED", e);
                }}
              /> */}
            </div>
            <div
              className={`field ${currentErrors.jotDescription ? "error" : ""}`}
            >
              <label>Remarks</label>
              <TextArea
                placeholder="Remarks"
                value={negotiationData.jotDescription}
                onChange={(e, data) =>
                  setNegotiationData((prevState) => ({
                    ...prevState,
                    jotDescription: data.value,
                  }))
                }
              />
              <small style={{ color: "#9f3a38" }}>{`${
                currentErrors.jotDescription ? currentErrors.jotDescription : ""
              }`}</small>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                justifyContent: "flex-end",
                gap: "5px",
                marginTop: "10px",
              }}
            >
              <Button
                template="reset"
                onClick={(e) => {
                  setButtonClicked(true);
                }}
              />
              <Button
                template="addOrSave"
                create={Object.keys(currentNegotiation).length === 0}
                onClick={onAddClick}
                isLoading={isAddLoading}
              />
            </div>

            <Grid
              heading="Negotiations"
              config={[
                { headerName: "Request", field: "jotRequestCode" },
                { headerName: "Job", field: "jotJobCode" },
                { headerName: "Stakeholder", field: "jotStakeholderCode" },
                {
                  headerName: "Charge Category",
                  field: "jotChargeCategoryValue",
                },
                {
                  headerName: "Charge Type",
                  field: "jotTypeValue",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                { headerName: "Charge Mode", field: "jotChargeModeValue" },
                {
                  headerName: "Charge",
                  field: "jotCharge",
                  cellDataType: "text",
                },
                {
                  headerName: "Qty",
                  field: "jotQty",
                  cellDataType: "text",
                },
                { headerName: "Active", field: "jotActiveValue" },
                {
                  field: "Edit",
                  filter: true,
                  cellRenderer: ButtonRenderer,
                  cellRendererParams: (params) => ({
                    clickHandler: () => {
                      setCurrentNegotiation(params.data);
                    },
                    label: "Edit",
                  }),
                },
                {
                  field: "Delete",
                  filter: true,
                  cellRenderer: DeleteButtonRenderer,
                  cellRendererParams: (params) => ({
                    consider: AUTH_TYPES.DELETE,
                    clickHandler: () => {
                      if (params.data.jotId) {
                        setDeletedList((prevState) => [
                          ...prevState,
                          params.data.jotId,
                        ]);
                      }
                      setNegotiations((prevNegotiations) =>
                        prevNegotiations.filter(
                          (negotiation) => negotiation.id !== params.data.id,
                        ),
                      );
                    },
                    label: "Delete",
                    className: "ui red button",
                    authenticated: true,
                    form: `${form}_${type}`,
                    module: MODULES.Transaction,
                    editMode:
                      params.data.jotId !== null && isNumber(params.data.jotId),
                  }),
                },
              ]}
              content={negotiations}
            />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                justifyContent: "flex-end",
                gap: "5px",
                marginTop: "10px",
              }}
            >
              <Button
                template="create"
                disabled={!stakeholderAvailable}
                onClick={submitHandler}
                isLoading={isSubmitLoading}
              />
            </div>
          </div>
        </form>
      )}
    </>
  );
};

export default JobMonitorNegotiation;
